/**
 * @wharfkit/transact-plugin-autocorrect v1.0.0
 * https://github.com/wharfkit/transact-plugin-autocorrect
 *
 * @license
 * Copyright (c) 2021 FFF00 Agents AB & Greymass Inc. All Rights Reserved.
 * 
 * Redistribution and use in source and binary forms, with or without modification,
 * are permitted provided that the following conditions are met:
 * 
 *  1. Redistribution of source code must retain the above copyright notice, this
 *     list of conditions and the following disclaimer.
 * 
 *  2. Redistribution in binary form must reproduce the above copyright notice,
 *     this list of conditions and the following disclaimer in the documentation
 *     and/or other materials provided with the distribution.
 * 
 *  3. Neither the name of the copyright holder nor the names of its contributors
 *     may be used to endorse or promote products derived from this software without
 *     specific prior written permission.
 * 
 * THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS "AS IS" AND
 * ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED
 * WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE ARE DISCLAIMED.
 * IN NO EVENT SHALL THE COPYRIGHT HOLDER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT,
 * INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING,
 * BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES; LOSS OF USE,
 * DATA, OR PROFITS; OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF
 * LIABILITY, WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE
 * OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED
 * OF THE POSSIBILITY OF SUCH DAMAGE.
 * 
 * YOU ACKNOWLEDGE THAT THIS SOFTWARE IS NOT DESIGNED, LICENSED OR INTENDED FOR USE
 * IN THE DESIGN, CONSTRUCTION, OPERATION OR MAINTENANCE OF ANY MILITARY FACILITY.
 */
import { Struct, Name, UInt32, Int64, Asset, AbstractTransactPlugin, TransactHookTypes, Action, prependAction } from '@wharfkit/session';
import { Resources } from '@wharfkit/resources';
import { __decorate } from 'tslib';

function getException(response) {
    if (response.processed.except) {
        return response.processed.except;
    }
    return null;
}

let Powerup = class Powerup extends Struct {
};
__decorate([
    Struct.field(Name)
], Powerup.prototype, "payer", void 0);
__decorate([
    Struct.field(Name)
], Powerup.prototype, "receiver", void 0);
__decorate([
    Struct.field(UInt32)
], Powerup.prototype, "days", void 0);
__decorate([
    Struct.field(Int64)
], Powerup.prototype, "net_frac", void 0);
__decorate([
    Struct.field(Int64)
], Powerup.prototype, "cpu_frac", void 0);
__decorate([
    Struct.field(Asset)
], Powerup.prototype, "max_payment", void 0);
Powerup = __decorate([
    Struct.type('powerup')
], Powerup);
let Buyrambytes = class Buyrambytes extends Struct {
};
__decorate([
    Struct.field(Name)
], Buyrambytes.prototype, "payer", void 0);
__decorate([
    Struct.field(Name)
], Buyrambytes.prototype, "receiver", void 0);
__decorate([
    Struct.field(UInt32)
], Buyrambytes.prototype, "bytes", void 0);
Buyrambytes = __decorate([
    Struct.type('buyrambytes')
], Buyrambytes);

var checking$3 = "Checking transaction";
var fee$3 = {
	title: "Accept Transaction Fee?",
	body: "Additional resources ({{resource}}) are required for your account to perform this transaction. Would you like to automatically purchase these resources from the network and proceed?",
	cost: "Cost of {{resource}}"
};
var resolving$3 = "Resolving transaction";
var en = {
	checking: checking$3,
	fee: fee$3,
	resolving: resolving$3
};

var checking$2 = "거래 확인";
var fee$2 = {
	title: "거래 수수료를 수락하시겠습니까?",
	body: "계정에서 이 거래를 수행하려면 추가 리소스({{resource}})가 필요합니다. 네트워크에서 이러한 리소스를 자동으로 구매하고 계속 진행하시겠습니까?",
	cost: "{{resource}}의 비용"
};
var resolving$2 = "트랜잭션 해결";
var ko = {
	checking: checking$2,
	fee: fee$2,
	resolving: resolving$2
};

var checking$1 = "正在检查交易";
var fee$1 = {
	title: "接受交易费用？",
	body: "您的帐户需要其他资源 （{{resource}}） 才能执行此交易。是否要自动从网络购买这些资源并继续？",
	cost: "{{resource}} 的成本"
};
var resolving$1 = "正在解析交易";
var zh_hans = {
	checking: checking$1,
	fee: fee$1,
	resolving: resolving$1
};

var checking = "正在檢查交易";
var fee = {
	title: "接受交易費用？",
	body: "您的帳戶需要其他資源 （{{resource}}） 才能執行此交易。是否要自動從網路購買這些資源並繼續？",
	cost: "{{resource}} 的成本"
};
var resolving = "正在解析交易";
var zh_hant = {
	checking: checking,
	fee: fee,
	resolving: resolving
};

var defaultTranslations = {
    en,
    ko,
    'zh-Hans': zh_hans,
    'zh-Hant': zh_hant,
};

var ChainFeatures;
(function (ChainFeatures) {
    /** eosio.buyram / eosio.buyrambytes https://github.com/EOSIO/eosio.contracts/blob/master/contracts/eosio.system/src/delegate_bandwidth.cpp#L43 */
    ChainFeatures[ChainFeatures["BuyRAM"] = 0] = "BuyRAM";
    /** FIO Bundled Transactions https://fio.wiki/knowledge-base/protocol/bundling-and-fees/ */
    // FIOBundledFees, // NYI
    /** eosio.powerup https://github.com/EOSIO/eosio.contracts/pull/397 */
    ChainFeatures[ChainFeatures["PowerUp"] = 1] = "PowerUp";
    /** eosio.rentcpu / eosio.rentnet https://github.com/EOSIO/eosio.contracts/blob/master/contracts/eosio.system/src/powerup.cpp */
    // REX, // NYI
    /** eosio.delegatebw https://github.com/EOSIO/eosio.contracts/blob/master/contracts/eosio.system/src/delegate_bandwidth.cpp#L372 */
    // Staking, // NYI
})(ChainFeatures || (ChainFeatures = {}));
const chains = {
    // EOS
    aca376f206b8fc25a6ed44dbdc66547c36c6c33e3a119ffbeaef943642f0e906: {
        features: [ChainFeatures.BuyRAM, ChainFeatures.PowerUp],
        sampleAccount: 'teamgreymass',
        symbol: Asset.Symbol.from('4,EOS'),
    },
    // Jungle 4
    '73e4385a2708e6d7048834fbc1079f2fabb17b3c125b146af438971e90716c4d': {
        features: [ChainFeatures.BuyRAM, ChainFeatures.PowerUp],
        sampleAccount: 'eosmechanics',
        symbol: Asset.Symbol.from('4,EOS'),
    },
};
/** Multiply all resource purchases to provide extra based on inaccurate estimates */
const multiplier = 1.5;
class TransactPluginAutoCorrect extends AbstractTransactPlugin {
    constructor() {
        super(...arguments);
        this.id = 'transact-plugin-autocorrect';
        this.translations = defaultTranslations;
        this.sample = null;
        this.price = null;
        this.resources = [];
        this.iterations = 0;
    }
    register(context) {
        if (!context.ui) {
            throw new Error('The TransactPluginAutoCorrect plugin requires a UI to be present.');
        }
        context.addHook(TransactHookTypes.beforeSign, async (request, context) => this.run(request, context));
    }
    async run(request, context) {
        // Abort if no UI is present
        if (!context.ui) {
            return { request };
        }
        // Reset internal state between transactions
        this.price = null;
        this.resources = [];
        this.iterations = 0;
        // Retrieve translation helper from the UI, passing the app ID
        const t = context.ui.getTranslate(this.id);
        // Notifify the UI that we are checking the transaction
        const checkingPromise = context.ui
            .prompt({
            title: t('checking', { default: 'Checking transaction' }),
            body: '',
            elements: [],
        })
            .catch((error) => {
            // Throw if what we caught was a cancelation
            if (error) {
                throw error;
            }
            // Otherwise return the original if no error occurred but this was rejected
            return { request };
        });
        // Attempt to correct this transaction
        const correctedPromise = this.correct(request, context);
        const modified = await Promise.race([checkingPromise, correctedPromise]);
        // If the request wasn't modified and no price exists, just return
        if (modified === request && !this.price) {
            return { request };
        }
        // Create unique set of resources that will be purchased
        const resources = Array.from(new Set(this.resources)).join('/');
        // Initiate a new cancelable prompt to inform the user of the fee required
        return context.ui
            .prompt({
            title: t('fee.title', { default: 'Accept Transaction Fee?' }),
            body: t('fee.body', {
                default: 'Additional resources ({{resource}}) are required for your account to perform this transaction. Would you like to automatically purchase these resources from the network and proceed?',
                resource: resources,
            }),
            elements: [
                {
                    type: 'asset',
                    data: {
                        label: t('fee.cost', {
                            default: 'Cost of {{resource}}',
                            resource: resources,
                        }),
                        value: this.price,
                    },
                },
                {
                    type: 'accept',
                },
            ],
        })
            .then(() => ({ request: modified }))
            .catch((error) => {
            // Throw if what we caught was a cancelation
            if (error) {
                throw error;
            }
            // Otherwise return the original if no error occurred but this was rejected
            return { request };
        });
    }
    async correct(request, context) {
        // TODO: Remove this once we are confident it won't create infinite loops against bad APIs
        // Keep track of how many interations have been done
        this.iterations++;
        if (this.iterations > 3) {
            throw new Error('Too many iterations. Please report this bug if you see it.');
        }
        // If the chain is not configured to correct issues or no UI is present, abort.
        const config = chains[String(context.chain.id)];
        if (!config || !context.ui) {
            return request;
        }
        // Set instance of resource library
        const resources = new Resources({
            api: context.client,
            sampleAccount: config.sampleAccount,
        });
        // Resolve any placeholders and complete the transaction for compute.
        const resolved = await context.resolve(request);
        // Call compute_transaction against the resolved transaction to detect any issues.
        const response = await context.client.v1.chain.compute_transaction(resolved.transaction);
        // Extract any exceptions from the response
        const exception = getException(response);
        if (exception) {
            switch (exception.name) {
                case 'tx_net_usage_exceeded': {
                    const { net_usage } = exception.stack[0].data;
                    const needed = net_usage * multiplier;
                    if (config.features.includes(ChainFeatures.PowerUp)) {
                        return this.powerup(context, resolved, resources, 0, needed);
                    }
                    break;
                }
                case 'tx_cpu_usage_exceeded': {
                    const { billed, billable } = exception.stack[0].data;
                    const needed = (billed - billable) * multiplier;
                    if (config.features.includes(ChainFeatures.PowerUp)) {
                        return this.powerup(context, resolved, resources, needed, 0);
                    }
                    break;
                }
                case 'ram_usage_exceeded': {
                    const { available, needs } = exception.stack[0].data;
                    const needed = (needs - available) * multiplier;
                    if (config.features.includes(ChainFeatures.BuyRAM)) {
                        return this.buyram(context, resolved, resources, needed);
                    }
                    break;
                }
            }
        }
        // Return the request
        return request;
    }
    async buyram(context, resolved, resources, needed) {
        // Get state of the blockchain and determine RAM price
        const config = chains[String(context.chain.id)];
        const ram = await resources.v1.ram.get_state();
        if (!this.sample) {
            this.sample = await resources.getSampledUsage();
        }
        // Determine price of resources
        const price = Asset.fromUnits(ram.price_per(needed) * 10000, config.symbol);
        // Keep a running total of the price
        if (this.price) {
            this.price.units.add(price.units);
        }
        else {
            this.price = price;
        }
        // And which resources are being paid for by this fee
        this.resources.push('RAM');
        // TODO: Implement maximum RAM fee to ensure potential bugs don't cause massive fees
        // How to determine a normal price per network?
        // const maxFee = 1
        // if (this.price.value > maxFee) {
        //     throw new Error('Fee is too high')
        // }
        // Create a new buyrambytes action to append
        const newAction = Action.from({
            account: 'eosio',
            name: 'buyrambytes',
            authorization: [resolved.signer],
            data: Buyrambytes.from({
                payer: resolved.signer.actor,
                receiver: resolved.signer.actor,
                bytes: needed,
            }),
        });
        // Create a new request based on this full transaction
        const newRequest = prependAction(resolved.request, newAction);
        // Attempt to correct the new request
        return this.correct(newRequest, context);
    }
    async powerup(context, resolved, resources, cpu, net) {
        // Get state of the blockchain and determine powerup price
        const config = chains[String(context.chain.id)];
        const powerup = await resources.v1.powerup.get_state();
        if (!this.sample) {
            this.sample = await resources.getSampledUsage();
        }
        // If powering up, always set a minimum to avoid API speed variance
        if (cpu < 5000) {
            cpu = 5000;
        }
        if (net < 10000) {
            net = 10000;
        }
        // Determine price of resources
        const price = Asset.from(Number(powerup.cpu.price_per(this.sample, cpu)) +
            Number(powerup.net.price_per(this.sample, net)), config.symbol);
        // Keep a running total of the price
        if (this.price) {
            this.price.units.add(price.units);
        }
        else {
            this.price = price;
        }
        // And which resources are being paid for by this fee
        this.resources.push('CPU', 'NET');
        // TODO: Implement maximum RAM fee to ensure potential bugs don't cause massive fees
        // How to determine a normal price per network?
        // const maxFee = 1
        // if (this.price.value > maxFee) {
        //     throw new Error('Fee is too high')
        // }
        // Create a new powerup action to append
        const newAction = Action.from({
            account: 'eosio',
            name: 'powerup',
            authorization: [resolved.signer],
            data: Powerup.from({
                payer: resolved.signer.actor,
                receiver: resolved.signer.actor,
                days: 1,
                net_frac: powerup.net.frac(this.sample, net),
                cpu_frac: powerup.cpu.frac(this.sample, cpu),
                max_payment: price,
            }),
        });
        // Create a new request based on this full transaction
        const newRequest = prependAction(resolved.request, newAction);
        // Attempt to correct the new request
        return this.correct(newRequest, context);
    }
}

export { TransactPluginAutoCorrect };
//# sourceMappingURL=transact-plugin-autocorrect.m.js.map
